import { useEffect, useState } from "react";

import type { IHttpClient } from "@/core/interfaces";
import { APIErrorException } from "@/core/services";
import { Registry } from "@/shared/infra/dependency-injection/registry";

const DEFAULT_MIME_TYPE = "image/jpeg";
/** Fotos buscadas em paralelo: tabela com muitas linhas não dispara todas de uma vez. */
const MAX_CONCURRENT_REQUESTS = 2;
/** Pausa da fila após 429/503 quando o serviço não informa `Retry-After`. */
const DEFAULT_PAUSE_MS = 30_000;
const MAX_PAUSE_MS = 300_000;

interface PhotoRequest {
  path: string;
  promise: Promise<string | null>;
  resolve: (url: string | null) => void;
  /** Componentes aguardando esta foto; sem nenhum, um pedido ainda na fila é descartado. */
  subscribers: number;
  started: boolean;
  generation: number;
}

/** Pedidos na fila ou em andamento por chave (path): vários avatares do mesmo path dividem um só. */
const requests = new Map<string, PhotoRequest>();
/** Só fotos ENCONTRADAS ficam guardadas; "sem foto" e falhas são tentados de novo depois. */
const resolvedPhotos = new Map<string, string>();
/** Pedidos ainda não iniciados, na ordem de chegada. */
const pending: PhotoRequest[] = [];

let running = 0;
/** Muda a cada `clearUserPhotoCache`: respostas de uma sessão anterior não entram no cache. */
let generation = 0;
let paused = false;
let pausedUntil = 0;
let resumeTimer: ReturnType<typeof setTimeout> | undefined;

function resume(): void {
  paused = false;
  pausedUntil = 0;
  resumeTimer = undefined;
  runNext();
}

/** Segura a fila (sem descartá-la) por `ms`; pausas sobrepostas valem pela mais longa. */
function pauseFor(ms: number): void {
  const until = Date.now() + ms;
  if (paused && until <= pausedUntil) return;

  clearTimeout(resumeTimer);
  paused = true;
  pausedUntil = until;
  resumeTimer = setTimeout(resume, ms);
}

/** Segundos do `Retry-After` (a forma de data HTTP não é usada: cai no padrão). */
function getPauseMs(error: APIErrorException): number {
  const header = error.headers?.["retry-after"]?.trim();
  const seconds = header ? Number(header) : Number.NaN;
  if (!Number.isFinite(seconds) || seconds < 0) return DEFAULT_PAUSE_MS;

  return Math.min(seconds * 1000, MAX_PAUSE_MS);
}

function isThrottled(error: unknown): error is APIErrorException {
  return error instanceof APIErrorException && (error.status === 429 || error.status === 503);
}

/** Qualquer resultado diferente de 200 com corpo (404, 503, rede...) significa "sem foto". */
async function fetchPhoto(path: string): Promise<string | null> {
  const http = Registry.getInstance().inject<IHttpClient>("HttpClient");
  const response = await http.get<ArrayBuffer>(path, { responseType: "arraybuffer" });

  if (response.status !== 200 || !response.data || response.data.byteLength === 0) return null;

  const type = response.headers?.["content-type"]?.split(";")[0]?.trim() || DEFAULT_MIME_TYPE;
  return URL.createObjectURL(new Blob([response.data], { type }));
}

function finish(request: PhotoRequest, url: string | null): void {
  if (request.generation !== generation) {
    if (url) URL.revokeObjectURL(url);
    request.resolve(null);
    return;
  }

  running -= 1;
  if (requests.get(request.path) === request) requests.delete(request.path);
  if (url) resolvedPhotos.set(request.path, url);

  request.resolve(url);
  runNext();
}

function start(request: PhotoRequest): void {
  running += 1;
  request.started = true;

  fetchPhoto(request.path).then(
    (url) => finish(request, url),
    (error: unknown) => {
      // Sobrecarga/limite: o pedido que falhou mostra as iniciais (sem retry) e a fila espera.
      if (isThrottled(error) && request.generation === generation) pauseFor(getPauseMs(error));
      finish(request, null);
    },
  );
}

function runNext(): void {
  while (!paused && running < MAX_CONCURRENT_REQUESTS && pending.length > 0) {
    const next = pending.shift();
    if (next) start(next);
  }
}

function acquire(path: string): PhotoRequest {
  const existing = requests.get(path);
  if (existing) {
    existing.subscribers += 1;
    return existing;
  }

  let resolve: (url: string | null) => void = () => undefined;
  const promise = new Promise<string | null>((done) => {
    resolve = done;
  });
  const request: PhotoRequest = {
    path,
    promise,
    resolve,
    subscribers: 1,
    started: false,
    generation,
  };

  requests.set(path, request);
  pending.push(request);
  runNext();
  return request;
}

/** Desiste de um pedido ainda na fila quando ninguém mais o aguarda (tela/linha desmontada). */
function release(request: PhotoRequest): void {
  request.subscribers -= 1;
  if (request.subscribers > 0 || request.started || request.generation !== generation) return;

  const index = pending.indexOf(request);
  if (index >= 0) pending.splice(index, 1);
  if (requests.get(request.path) === request) requests.delete(request.path);
  request.resolve(null);
}

/** Descarta as fotos em cache (revogando as URLs de blob). Chamar ao encerrar a sessão. */
export function clearUserPhotoCache(): void {
  for (const url of resolvedPhotos.values()) {
    URL.revokeObjectURL(url);
  }
  resolvedPhotos.clear();
  requests.clear();
  for (const request of pending.splice(0)) request.resolve(null);

  generation += 1;
  running = 0;
  paused = false;
  pausedUntil = 0;
  clearTimeout(resumeTimer);
  resumeTimer = undefined;
}

/**
 * Foto autenticada do usuário como URL de blob, ou `null` (sem foto, carregando, erro).
 * Qualquer falha resulta em `null` — a tela cai nas iniciais. Só a foto encontrada fica em
 * cache de memória por chave (ao voltar à tela não há nova requisição); "sem foto" e erros são
 * tentados de novo na próxima montagem. Requisições simultâneas do mesmo path são unificadas,
 * no máximo 2 correm em paralelo, 429/503 pausam a fila (respeitando `Retry-After`) e um pedido
 * ainda na fila é descartado quando ninguém mais o aguarda. A URL não é revogada ao desmontar
 * o componente (outros consumidores podem usá-la), e sim em `clearUserPhotoCache`.
 */
export function useUserPhoto(path: string | null | undefined, enabled = true): string | null {
  const active = enabled && Boolean(path);
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!active || !path || resolvedPhotos.has(path)) return;

    let cancelled = false;
    const request = acquire(path);
    void request.promise.then(() => {
      if (!cancelled) setVersion((value) => value + 1);
    });

    return () => {
      cancelled = true;
      release(request);
    };
  }, [active, path]);

  return active && path ? (resolvedPhotos.get(path) ?? null) : null;
}
